/**
 * Component: AdminDrawer
 * @description A customizable drawer component for administrative tools, featuring a title, close button, and content area.
 *
 * @props
 *   @param {boolean} isOpen - Determines whether the drawer is open and visible.
 *   @param {function} onClose - Callback function triggered when the drawer is closed.
 *   @param {function} onBack - Callback function triggered when the back button is clicked.
 *   @param {string} title - The title displayed in the drawer header.
 *   @param {ReactNode} children - The content to be displayed inside the drawer.
 *   @param {boolean} showCloseButton - Determines whether the close button is visible.
 *
 * @styles
 *   - Drawer adjusts its width and visibility based on `isOpen` class.
 *   - Content adjusts padding based on presence of title.
 *   - Includes a close button and scrollable content area with a maximum height of 90% of the viewport.
 *
 * @example
 * <AdminDrawer
 *   isOpen={isDrawerOpen}
 *   onClose={() => setDrawerOpen(false)}
 *   title="Shift Details"
 *   showCloseButton={false}
 * >
 *   <p>Drawer content goes here...</p>
 * </AdminDrawer>
 */

import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BackIcon from '@mui/icons-material/ArrowBack';
import { DrawerContainer, DrawerHeader, StyledIconButton, DrawerContent } from './AdminDrawer.styles';

const AdminDrawer = ({ isOpen, onClose, title, children, onBack, showCloseButton = true }) => {
  return (
    <DrawerContainer className={isOpen ? 'open' : 'closed'}>
      <DrawerHeader>
        {onBack && (
          <StyledIconButton onClick={onBack}>
            <BackIcon />
          </StyledIconButton>
        )}
        <Typography variant="h6">{title}</Typography>
        {showCloseButton && (
          <StyledIconButton onClick={onClose}>
            <CloseIcon />
          </StyledIconButton>
        )}
      </DrawerHeader>
      <DrawerContent>{children}</DrawerContent>
    </DrawerContainer>
  );
};

AdminDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  children: PropTypes.node,
  onBack: PropTypes.func,
  fullHeight: PropTypes.bool,
  showCloseButton: PropTypes.bool,
};

export default AdminDrawer;
